/**
 * Query Limit Modal - Handles Login Wall
 * Google + Email OTP login options
 * Works on all browsers
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Crown,
  Zap,
  CheckCircle,
  Loader2,
  History,
  Shield,
  Mail,
  Bell
} from 'lucide-react';
import { Button } from "./ui/button";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

// TaxSetu Logo
const TAXSETU_LOGO = "https://customer-assets.emergentagent.com/job_taxgpt-india-1/artifacts/eq72g3c3_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const QueryLimitModal = ({ 
  isOpen, 
  onClose, 
  type,
  onGoogleLogin,
  queriesUsed,
  maxQueries
}) => {
  const navigate = useNavigate();
  const { loginWithGoogle, handleOneTapCallback } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authMethod, setAuthMethod] = useState('google');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Initialize Google One-Tap
  useEffect(() => {
    if (!isOpen) return;
    
    const initGoogleOneTap = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google/client-id`);
        const data = await response.json();
        
        if (data.client_id && window.google?.accounts?.id) {
          window.google.accounts.id.initialize({
            client_id: data.client_id,
            callback: handleOneTapResponse,
            auto_select: true,
            cancel_on_tap_outside: false,
            context: 'signin',
            ux_mode: 'popup',
            use_fedcm_for_prompt: true
          });
          
          window.google.accounts.id.prompt();
        }
      } catch (err) {
        console.error('Failed to initialize Google One-Tap:', err);
      }
    };
    
    initGoogleOneTap();
  }, [isOpen]);

  const handleOneTapResponse = async (response) => {
    setLoading(true);
    try {
      await handleOneTapCallback(response);
      onClose();
    } catch (err) {
      setError('Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      await loginWithGoogle();
    } catch (err) {
      setError('Sign-in failed. Please try again.');
      setLoading(false);
    }
  };

  // Email OTP functions
  const handleSendOTP = async () => {
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setOtpLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send OTP');
      }
      
      setOtpSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    setOtpLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/email/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid OTP');
      }
      
      localStorage.setItem('taxsetu_token', data.access_token);
      localStorage.setItem('taxsetu_user', JSON.stringify(data.user));
      window.location.reload();
      
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-[#0a0f1a] rounded-2xl shadow-2xl overflow-hidden border border-slate-800"
        >
          {/* Glow effect */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-5 md:p-6">
            {/* Logo and Title */}
            <div className="text-center mb-4">
              <img 
                src={TAXSETU_LOGO} 
                alt="TaxSetu AI" 
                className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-2 object-contain"
              />
              <h2 className="text-xl md:text-2xl font-bold text-white mb-0.5">
                Tax<span className="text-cyan-400">Setu</span> AI
              </h2>
              <p className="text-slate-400 text-xs md:text-sm">Your AI-Powered Tax Expert</p>
            </div>

            {/* Sign in to continue banner */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-2.5 md:p-3 mb-3 text-center">
              <p className="text-cyan-400 font-medium text-sm mb-0.5">
                Sign in to continue
              </p>
              <p className="text-slate-400 text-xs">
                Get unlimited access to TaxSetu AI
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-1.5 md:space-y-2 mb-3">
              <BenefitItem icon={Zap} text="Unlimited tax queries" />
              <BenefitItem icon={History} text="Save your search history" />
              <BenefitItem icon={Shield} text="Safe and Private" />
            </div>

            {/* Auth Method Tabs */}
            <div className="flex mb-3 bg-slate-800/50 rounded-lg p-1">
              <button
                onClick={() => { setAuthMethod('google'); setError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  authMethod === 'google' 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                </svg>
                Google
              </button>
              <button
                onClick={() => { setAuthMethod('email'); setError(''); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  authMethod === 'email' 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Mail className="w-4 h-4" />
                Email OTP
              </button>
            </div>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            {/* Fixed height container for auth options */}
            <div className="min-h-[130px]">
              {authMethod === 'google' ? (
              /* Google Sign-In */
              <div className="space-y-2">
                <Button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full bg-white hover:bg-gray-50 text-gray-800 font-medium py-2.5 text-sm"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      Continue with Google
                    </>
                  )}
                </Button>
                <p className="text-center text-slate-500 text-xs">
                  Works on all browsers
                </p>
              </div>
            ) : (
              /* Email OTP Sign-In */
              <div className="space-y-2">
                {!otpSent ? (
                  <>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 text-sm"
                    />
                    <Button
                      onClick={handleSendOTP}
                      disabled={otpLoading || !email}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2.5 text-sm"
                    >
                      {otpLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Bell className="w-5 h-5 mr-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                    <p className="text-center text-slate-500 text-xs">
                      Works with Gmail & all email providers
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-center text-green-400 text-xs">
                      ✓ OTP sent to {email}
                    </p>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 text-center text-lg tracking-widest"
                    />
                    <Button
                      onClick={handleVerifyOTP}
                      disabled={otpLoading || otp.length !== 6}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2.5 text-sm"
                    >
                      {otpLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Verify & Sign In
                        </>
                      )}
                    </Button>
                    <button
                      onClick={() => { setOtpSent(false); setOtp(''); }}
                      className="w-full text-slate-400 text-xs hover:text-white"
                    >
                      Change email / Resend OTP
                    </button>
                  </>
                )}
              </div>
            )}
            </div>

            {/* Terms */}
            <div className="text-center mt-3 pt-3 border-t border-slate-800">
              <p className="text-white/80 font-medium text-xs mb-1">
                ✨ One-tap login, let's start your tax journey!
              </p>
              <p className="text-xs text-slate-600">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-cyan-400 hover:underline">Terms</a>
                {' '}&{' '}
                <a href="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

// Benefit item component
function BenefitItem({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-slate-300">
      <div className="w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3 h-3 text-cyan-400" />
      </div>
      <span className="text-xs md:text-sm">{text}</span>
    </div>
  );
}

export default QueryLimitModal;
